import type { BillingStatus } from "@io-spoke/contracts/billing";
import { CalendarClock, ShieldCheck } from "lucide-react";

function formatDate(value?: string) {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

function TrialRetentionNotice({ retention }: { retention: NonNullable<BillingStatus["trialRetention"]> }) {
  if (retention.state === "eligible_for_review") {
    return <p className="mt-3 text-sm text-muted-foreground">Your guaranteed retention period ended {formatDate(retention.retainUntil)}. Add payment to resume while your workspace data is still available.</p>;
  }
  return <p className="mt-3 text-sm text-muted-foreground">Your workspace data is retained through {formatDate(retention.retainUntil)}. Add payment before then to restore full access to the same workspace. After that date, retention is no longer guaranteed.</p>;
}

export function BillingAccessSummary({ billing }: { billing: BillingStatus }) {
  if (billing.status === "trialing") {
    return <div><p className="text-4xl font-semibold tracking-tight">{billing.daysRemaining} days left</p><p className="mt-2 text-sm text-muted-foreground">Your trial ends {formatDate(billing.trialEndsAt)}. Add payment any time to keep IO Spoke running.</p></div>;
  }
  if (billing.status === "active") {
    return <div className="flex gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-400" aria-hidden="true" /><div><p className="font-medium">Your workspace is covered.</p><p className="mt-1 text-sm text-muted-foreground">Next billing period ends {formatDate(billing.currentPeriodEndsAt)}.</p></div></div>;
  }
  return (
    <div className="flex gap-3">
      <CalendarClock className="mt-0.5 h-5 w-5 text-amber-400" aria-hidden="true" />
      <div>
        <p className="font-medium">New analysis and changes are paused.</p>
        <p className="mt-1 text-sm text-muted-foreground">Your existing customer intelligence remains available. Add payment to resume.</p>
        {billing.status === "expired" && billing.trialRetention ? <TrialRetentionNotice retention={billing.trialRetention} /> : null}
      </div>
    </div>
  );
}
